import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import { AppToolbar } from "../components/AppToolbar";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import RoleUtils from "../utils/RoleUtils";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import SiteService from "../services/SiteService";
import Site from "../models/Site";
import AddIcon from "@mui/icons-material/Add";
import { SelectComponent } from "../components/SelectComponent";
import { SitesRoleOptions } from "../utils/enums/EnsolversStaffRoles";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import OpsStringUtils from "../utils/OpsStringUtils";
import SiteCreateDialog from "../components/AdminSite/SiteCreateDialog";
import { AppDialog } from "../components/AppDialog";
import { useSiteList } from "../hooks/useSiteList";
import SiteViewDialog from "../components/AdminSite/SiteViewDialog";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import SiteEditDialog from "../components/AdminSite/SiteEditDialog";
import ImageService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/ImageService";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import { randomId } from "../utils/FileUtils";
import { SwitchCustom } from "../utils/SwitchCustom";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { makeStyles } from "@material-ui/core";
import useWindowSize from "../hooks/useWindowSize";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { ColorUtils } from "../utils/ColorUtils";

export default function SiteList() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [search, setSearch] = useState<string>("");
  const [staffRole, setStaffRole] = useState<string>("All");
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>({ label: "All", externalId: "All" });
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [update, setUpdate] = useState<number>();
  const { siteActions } = useSiteList();
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);
  const [sitesFromArchivedProjects, setSitesFromArchivedProjects] = useLocalStorage<boolean>(
    STORAGE_KEYS.STORED_SHOW_ARCHIVED_PROJECTS,
    false
  );
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const searchParams = new URLSearchParams(useLocation().search);
  const titleFilter = searchParams.get("title");
  const roleFilter = searchParams.get("role");
  const project = searchParams.get("project");
  const archived = searchParams.get("archived");
  const {updateUrl} = useUpdateUrl(Routes.SITE, searchParams);

  useEffect(() => {
    if (project !== null && siteActions.projects) {
      const projectUrl = siteActions.projects.find((p) => p.externalId === project);
      if (projectUrl) setSelectedProject(projectUrl);
    } else {
      searchParams.set("project", "All");
    }

    if (roleFilter !== null) {
      setStaffRole(roleFilter);
    } else {
      searchParams.set("role", "All");
    }

    if (titleFilter !== null && titleFilter !== "") {
      setSearch(titleFilter);
    }

    if (archived !== null) {
      setSitesFromArchivedProjects(archived === "true");
    } else if (sitesFromArchivedProjects !== undefined) {
      searchParams.set("archived", sitesFromArchivedProjects.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteActions.projects]);

  useEffect(() => {
    siteActions.getProjectNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSitesFromArchivedProjects = () => {
    setSitesFromArchivedProjects((prev) => !prev);
    searchParams.set("archived", (!sitesFromArchivedProjects).toString());
    updateUrl();
  };

  const handleOnUpdate = (instance: Site) => {
    return SiteService.editSite(instance)
      .then(() => {
        setUpdate(Date.now());
        NotificationService.info("Site updated successfully");
        return true;
      })
      .catch((error) => {
        NotificationService.error(error.response.data.message);
        return false;
      });
  };

  const handleOnCreate = () => {};

  const onCloseSiteCreateDialog = () => {
    setOpenCreateModal(false);
    setSelectedFile(undefined);
  };

  const onCloseSiteEditDialog = () => {
    siteActions.setEditSite(undefined);
    setSelectedFile(undefined);
  };

  const handleImageDelete = () => {
    setSelectedFile("");
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const fileList = e.target.files;

    if (!fileList) return;

    let fileToUpload: File = fileList[0];
    const filename: string = `ensolvers-ops-site-image_${randomId()}`;
    ImageService.uploadImage(fileToUpload, filename, undefined)
      .then((response) => {
        if (response.succeeded()) {
          const iconUrl = response.getContent().url;

          setSelectedFile(iconUrl);
          setIsLoading(false);
        }
      })
      .catch((e) => {
        NotificationService.error("This file can not be uploaded. Please try with a different one");
        setIsLoading(false);
      });
  };

  const handleOnCreateSite = async (site: Site) => {
    try {
      await SiteService.create(site);
      NotificationService.info("Site created successfully");
    } catch (error) {
      NotificationService.error("Error trying to create site");
    }
    setSelectedFile("");
  };

  const renderSearchSiteTitle = (
    <TextField
      label="Site title"
      name="sitetitle"
      variant="outlined"
      value={search}
      size="small"
      onChange={(e: any) => {
        setSearch(e.target.value);
        searchParams.set("title", e.target.value);
        updateUrl();
      }}
      style={{ marginLeft: "20px", marginRight: "20px", minWidth: "400px" }}
    />
  );
  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderSearchSiteTitle}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderSearchSiteTitle}
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Roles"}
        name="roles"
        value={staffRole}
        setValue={(role: string) => {
          setStaffRole(role);
          searchParams.set("role", role);
          updateUrl();
        }}
        options={Object.values(SitesRoleOptions)}
        size="small"
      />
      <Autocomplete
        disablePortal
        options={
          RoleUtils.isAdmin()
            ? [{ label: "All", externalId: "All" }, ...siteActions.projects]
            : [{ label: "All", externalId: "All" }, ...siteActions.projectsManager]
        }
        getOptionLabel={(option) => option.label}
        className="project-name-select"
        size="small"
        value={selectedProject}
        onChange={(event, newValue) => {
          if (newValue) {
            setSelectedProject(newValue);
            searchParams.set("project", newValue.externalId);
            updateUrl();
          }
        }}
        renderInput={(params) => <TextField {...params} label="Project Name" variant="outlined" />}
        disableClearable
      />

      <Box marginLeft="1em">
        <SwitchCustom
          checked={sitesFromArchivedProjects}
          onChange={toggleSitesFromArchivedProjects}
          size="medium"
          color="primary"
          label="Archived"
        />
      </Box>
    </>
  );


  function getAdditionalActions(instance: Site): ActionsMenuEntryIcons[] {
    if (!RoleUtils.isAdminOrManager()) return (
      [{
        title: t("N/A"),
        action: () => {},
      }]
    )
    const options = [
      {
        title: t("View"),
        action: () => siteActions.handleOpenViewDialog(instance),
        icon:<VisibilityIcon/>
      }, 
      {
        title: t("Edit"),
        action: () => siteActions.handleOpenEditDialog(instance),
        icon:<EditIcon/>
      },
      {
        title: t("Delete"),
        action: () => siteActions.setSelectedSite(instance),
        icon:<DeleteOutlineIcon/>
      },      
    ];

    return options;
  }

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={<AppToolbar title={t("Sites")}>{renderFilters()}</AppToolbar>}
      bottomSection={
        <Box sx={{ "&>div:first-child": { boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" } }}>
          <TableBasedCrud<Site>
            buttonContainer={ customButtonsContainer }
            gridButtonContainer={ gridButtonContainer }
            elementName={"Sites"}
            maxHeight={calculateMaxHeight(true)}
            stickyHeader
            stickyPagination
            delayFetch={search}
            rowsPerPage={ROWS_PER_PAGE}
            denseTable
            forceUpdateOn={update}
            setIsEditModeActive={siteActions.setEditMode}
   
            stylesForRowIfCondition={(instance) => (instance.archived ? { backgroundColor: "#F1F0F0" } : {})}
            renderRowIfCondition={(instance) => sitesFromArchivedProjects || !instance.archived}
            onDataFetch={(pageNumber, count) =>
              SiteService.getSiteFilter(pageNumber, count, selectedProject!.externalId || "All", staffRole, search, sitesFromArchivedProjects).then((result: any) =>
                result.getContent()
              )
            }
            onCreate={RoleUtils.isAdminOrManager() ? () => handleOnCreate() : undefined}
            validateBeforeClosing
            addButtonBuilder={
              RoleUtils.isAdminOrManager()
                ? () => (
                    <Button
                      startIcon={<AddIcon />}
                      sx={{ color: "black" }}
                      onClick={() => {
                        setOpenCreateModal(true);
                      }}
                    >
                      <Typography variant="body1">Add New Site</Typography>
                    </Button>
                  )
                : undefined
            }
            onViewMode={(instance) => siteActions.handleOpenViewDialog(instance)}
            buildAdditionalActions={(instance, refresh) => {
              return [
                {
                  title: "Edit",
                  action: () => siteActions.handleOpenEditDialog(instance),
                },
                {
                  title: t("Delete"),
                  action: () => {
                    siteActions.setSelectedSite(instance);
                  },
                },
              ];
            }}
            renderDialogContent={(register, errors, control) => <></>}
            resetValue={{ Role: null, Project: null, Title: null }}
            tableColumns={[
              {
                header: t("Image"),
                content: (instance) => (
                  <>
                    <OpsAvatarComponent
                      avatarUrl={instance.imageUrl}
                      forceGenericAvatar={true}
                      genericAvatar={<NoPhotographyIcon />}
                      sx={{ width: "2.5em", height: "2.5em" }}
                    />
                  </>
                ),
              },
              {
                header: t("Role"),
                content: (instance) => (
                  // This property should never be undefined, but if sometime this receives an undefined this conditional will prevent the view from crashing, but showing that the site has no role assigned
                  <>{OpsStringUtils.capitalize(instance.roleName != undefined ? SitesRoleOptions[instance.roleName as keyof typeof SitesRoleOptions] : "No role assigned")}</>
                ),
              },
              {
                header: t("Project"),
                content: (instance) => <>{instance.projectExternalId === "All" ? "ALL" : instance.projectName}</>,
              },
              {
                header: t("Title"),
                content: (instance) => <>{instance.title!}</>,
              },
              {
                header: t("Actions"),
                content: (instance) => {
                  const actions = getAdditionalActions(instance)
                  return (<>
                    {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                    </>
                  )
                },
              },              
            ]}
            hideActions={true}  // Hide default Actions column
          />
          {/* Dialog to create sites */}
          <SiteCreateDialog
            onClose={onCloseSiteCreateDialog}
            staffRole={staffRole}
            isLoading={isLoading}
            open={openCreateModal}
            projects={siteActions.projectsAdminManager}
            handleUploadImage={handleUploadImage}
            selectedFile={selectedFile ? selectedFile : ""}
            handleOnCreateSite={handleOnCreateSite}
            handleImageDelete={handleImageDelete}
          />
          {/* Dialog to view site */}
          {siteActions.viewSite && (
            <SiteViewDialog viewSite={siteActions.viewSite} onClose={() => siteActions.setViewSite(undefined)} isLoading={false} />
          )}
          {/* Dialog to edit site */}
          <SiteEditDialog
            onClose={() => onCloseSiteEditDialog()}
            handleOnUpdate={handleOnUpdate}
            open={!!siteActions.editSite}
            isLoading={isLoading}
            projects={siteActions.projectsAdminManager}
            handleImageDelete={handleImageDelete}
            handleUploadImage={handleUploadImage}
            selectedFile={selectedFile}
            editSite={siteActions.editSite}
          />
          {/* Dialog to delete sites */}
          <AppDialog
            open={!!siteActions.selectedSite}
            onClose={() => siteActions.setSelectedSite(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The next site is going to be eliminated: ${siteActions.selectedSite?.title}. 
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[
              {
                label: "Confirm delete",
                onClick: () => {
                  siteActions.handleOnDeleteSite();
                },
              },
            ]}
          />
          {/* Dialog to manage filters  */}
          <FilterDialog title={"Site"} setIsOpen={setIsOpen} isOpen={isOpen}>
            {renderDialogFilters}
          </FilterDialog>
        </Box>
      }
    />
  );
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    borderRadius: "4px",
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    "& > label, div:first-child": {
      margin: "0 !important",
      minWidth: "0!important",
      width: "100%",
    },
    "& > div ": {
      margin: "0!important",
    },
  },
  mobileFilters: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    "&> div": {
      margin: "0 !important",
    },
    "&>div:first-child": {
      minWidth: "0 !important",
    },
  },
  [theme.breakpoints.down("md")]: {
    desktopFilters: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr 1fr",
      gridRowGap: "10px",
      gridColumnGap: "10px",
    },
  },
}),
{ index: 1 }
);
